import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { filter, switchMap } from 'rxjs/operators'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import {
  Destroyable,
  ConfirmDialogStatus,
  ConfirmDialogService,
  untilDestroy,
  DataCheckerService,
} from '@tokeet-frontend/tv3-platform'
import { AiResponderSettings } from '../store/ai-settings.model'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import * as lodash from 'lodash'
import { selectAISettings } from '../store/ai-settings.selectors'
import { UpdateAISettings } from '../store/ai-settings.actions'
import { AISettingsGuard } from '../store/ai-settings.guard'

@Component({
  selector: 'app-ignore-ai-toggle',
  templateUrl: './ignore-ai-toggle.component.html',
  styleUrls: ['./ignore-ai-toggle.component.scss'],
})
export class IgnoreAIToggleComponent extends Destroyable implements OnInit, OnChanges {
  @Input() type: 'guest' | 'booking' | 'rental' | 'domain'
  @Input() item: { id: string }
  @Input() label: string
  @Input() view: 'button' | 'switch' = 'switch'
  /** Optional: pass AI settings from parent to avoid fetching per row */
  @Input() settings: AiResponderSettings = null

  tooltip: string
  @Input() set toggleTooltip(tooltip: string) {
    this.tooltip = tooltip
  }
  @Input() tooltipPosition = 'above'

  isAISubInactive = true

  aiSettings: AiResponderSettings = null

  get ignoreListKey() {
    return this.type === 'domain' ? 'ignoreGuestsRegexp' : `ignore${lodash.capitalize(this.type)}s`
  }
  constructor(
    private store: Store<any>,
    private confirmDialog: ConfirmDialogService,
    private dataChecker: DataCheckerService
  ) {
    super()
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If settings are passed from parent, use them directly
    if (changes.settings && this.settings) {
      this.aiSettings = this.settings
      this.isAISubInactive = false
    }
  }

  ngOnInit(): void {
    // Only fetch AI settings if not provided via input
    if (!this.settings) {
      this.dataChecker.check([AISettingsGuard])
      this.store
        .pipe(
          select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)),
          filter((res) => res?.isValid),
          switchMap(() => this.store.pipe(select(selectAISettings))),
          untilDestroy(this)
        )
        .subscribe((settings) => {
          this.aiSettings = settings
          this.isAISubInactive = false
        })
    }
  }

  getToggleTooltip(isAISubInactive: boolean): string {
    if (!isAISubInactive) {
      return (
        this.tooltip || $localize`Exclude this ` + this.type + $localize` from Advance Intelligence automated messages`
      )
    }
    return $localize`Go to Appstore to subscribe for this`
  }

  isAIActive() {
    return !this.aiSettings?.[this.ignoreListKey]?.includes(this.item?.id)
  }

  onToggleAI(checked: boolean, toggle?: MatSlideToggle) {
    const confirm = () =>
      this.confirmDialog
        .openRegular({
          title: $localize`Warning`,
          body:
            $localize`Do you want to exclude this ` +
            this.type +
            $localize` from Advance Intelligence automated responses?`,
        })
        .pipe(
          filter((status) => {
            if (status === ConfirmDialogStatus.Confirmed) {
              return true
            }
            if (toggle) toggle.checked = !checked
            return false
          })
        )

    const doAction = () => {
      let ignoreList = this.aiSettings?.[this.ignoreListKey] || []
      if (checked) {
        ignoreList.push(this.item.id)
      } else {
        ignoreList = ignoreList.filter((id) => id !== this.item.id)
      }
      this.store.dispatch(
        UpdateAISettings({
          payload: { [this.ignoreListKey]: ignoreList },
        })
      )
    }

    if (checked) {
      confirm().subscribe(() => doAction())
    } else {
      doAction()
    }
  }
}
